import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from './Shared';
import { patientProfileAPI } from '../services/api';

// Ιδιωτικές Συνταγές tab on the admin patient profile.
// Same pattern as PatientReferralLettersTab and PatientFitNotesTab: the PDF is
// fetched through the backend proxy so every view lands in activity_logs.
// Drug lines come back on the row as `items` (aggregated server-side).

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('el-GR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function PatientPrivatePrescriptionsTab({ patientId }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const openedRef = useRef([]);

  useEffect(() => {
    return () => {
      openedRef.current.forEach((url) => URL.revokeObjectURL(url));
      openedRef.current = [];
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await patientProfileAPI.getPrivatePrescriptions(patientId);
      setPrescriptions(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Αποτυχία φόρτωσης ιδιωτικών συνταγών');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const openPdf = async (prescriptionId) => {
    setDownloading(prescriptionId);
    try {
      const res = await patientProfileAPI.getPrivatePrescriptionPdfBlob(prescriptionId);
      const blobUrl = URL.createObjectURL(res.data);
      openedRef.current.push(blobUrl);
      window.open(blobUrl, '_blank', 'noopener');
    } catch (err) {
      alert('Αποτυχία ανοίγματος PDF');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Card title="💊 Ιδιωτικές Συνταγές">
        <p style={{ color: '#6B7280' }}>Φόρτωση…</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="💊 Ιδιωτικές Συνταγές">
        <p style={{ color: '#DC2626' }}>{error}</p>
      </Card>
    );
  }

  if (prescriptions.length === 0) {
    return (
      <Card title="💊 Ιδιωτικές Συνταγές">
        <p style={{ color: '#6B7280' }}>Δεν έχουν εκδοθεί ιδιωτικές συνταγές για αυτόν τον ασθενή.</p>
      </Card>
    );
  }

  return (
    <Card title={`💊 Ιδιωτικές Συνταγές (${prescriptions.length})`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {prescriptions.map((p) => {
          const cancelled = p.status === 'cancelled';
          const items = Array.isArray(p.items) ? p.items : [];
          return (
            <div
              key={p.id}
              style={{
                border: `1px solid ${cancelled ? '#FCA5A5' : '#E5E7EB'}`,
                borderRadius: 8,
                padding: 16,
                background: cancelled ? '#FEF2F2' : '#FFF',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {p.prescription_number || '—'}
                    {p.version > 1 && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#6B7280' }}>
                        (έκδοση {p.version})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                    Δρ. {p.doctor_first_name} {p.doctor_last_name}
                    {p.doctor_specialty ? ` · ${p.doctor_specialty}` : ''}
                  </div>
                </div>
                {cancelled && (
                  <span style={{
                    background: '#DC2626', color: '#FFF', fontSize: 11, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                  }}>
                    ΑΚΥΡΩΘΗΚΕ
                  </span>
                )}
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 8, marginTop: 12, fontSize: 13,
              }}>
                <div>
                  <span style={{ color: '#6B7280' }}>Ημ. έκδοσης: </span>
                  <strong>{fmtDateTime(p.issued_at || p.created_at)}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Ισχύει έως: </span>
                  <strong>{fmtDate(p.valid_until)}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Χώρα: </span>
                  <strong>{p.destination_country || '—'}</strong>
                </div>
                <div>
                  <span style={{ color: '#6B7280' }}>Ραντεβού: </span>
                  <strong>{fmtDate(p.appointment_date)} {String(p.start_time || '').slice(0, 5)}</strong>
                </div>
              </div>

              {p.diagnosis && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <span style={{ color: '#6B7280' }}>Διάγνωση: </span>
                  {p.diagnosis}
                </div>
              )}

              {items.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, fontWeight: 600 }}>
                    ΦΑΡΜΑΚΑ ({items.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {items.map((it, idx) => (
                      <div
                        key={it.id || idx}
                        style={{
                          background: '#F9FAFB', borderRadius: 6, padding: '8px 10px', fontSize: 13,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {it.inn || '—'}
                          {it.strength ? ` ${it.strength}` : ''}
                          {it.brand_name ? ` (${it.brand_name})` : ''}
                        </div>
                        {(it.pharmaceutical_form || it.quantity || it.dosage_regimen) && (
                          <div style={{ color: '#6B7280', marginTop: 2 }}>
                            {[it.pharmaceutical_form, it.quantity ? `× ${it.quantity}` : null, it.dosage_regimen]
                              .filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {it.substitution_allowed === false && (
                          <div style={{ color: '#991B1B', marginTop: 2, fontSize: 12 }}>
                            Δεν επιτρέπεται υποκατάσταση
                          </div>
                        )}
                        {it.notes && (
                          <div style={{ color: '#6B7280', marginTop: 2, fontStyle: 'italic' }}>
                            {it.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cancelled && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#991B1B' }}>
                  Ακυρώθηκε {fmtDateTime(p.cancelled_at)}
                  {p.cancelled_reason ? ` — ${p.cancelled_reason}` : ''}
                </div>
              )}

              <div style={{
                marginTop: 12, paddingTop: 12, borderTop: '1px solid #F3F4F6',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {p.email_sent_at
                    ? `Εστάλη ${fmtDateTime(p.email_sent_at)}${p.email_recipient ? ` → ${p.email_recipient}` : ''}`
                    : 'Δεν έχει σταλεί με email'}
                </div>
                <button
                  onClick={() => openPdf(p.id)}
                  disabled={downloading === p.id}
                  style={{
                    background: '#C0185A', color: '#FFF', border: 'none',
                    borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600,
                    cursor: downloading === p.id ? 'default' : 'pointer',
                    opacity: downloading === p.id ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {downloading === p.id ? 'Άνοιγμα…' : 'Άνοιγμα PDF'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
